import React, { useState, useContext, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { useDropzone } from "react-dropzone";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckMarkIcon from "../../../assets/icons/CheckMarkIcon";
import ErrorIcon from "../../../assets/icons/ErrorIcon";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import DownloadDocumentCell from "../../CustomizedTable/CustomCells/DownloadDocumentCell/DownloadDocumentCell";
import LoadProfileUploadDocumentModal from "./LoadProfileUploadDocumentModal/LoadProfileUploadDocumentModal";
import { ImageService } from "../../../services/ImageService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function LoadProfileDocuments({ activeLoad, carrierExtraPay }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [loadDocuments, setLoadDocuments] = useState([]);
  const [hasNoDocuments, setHasNoDocuments] = useState(false);
  const [isBol, setIsBol] = useState(false);
  const [isPod, setIsPod] = useState(false);
  const [isInvoice, setIsInvoice] = useState(false);
  const [isLumper, setIsLumper] = useState(false);
  const [refreshDocuments, setRefreshDocuments] = useState(false);
  const { acceptedFiles, getRootProps } = useDropzone({ noClick: true, noKeyboard: true, maxFile: 1 });

  const documents = useMemo(() => loadDocuments, [loadDocuments]);

  const columns = useMemo(
    () => [
      {
        Header: "Document Name",
        accessor: "descr",
        filter: "fuzzyText",
        orderId: activeLoad.id,
        width: 400,
        Cell: DownloadDocumentCell,
      },
      {
        Header: "Uploaded By",
        accessor: "uploadedBy",
        filter: "fuzzyText",
        width: 140,
        Cell: DefaultCell,
      },
      {
        Header: "Date & Time",
        accessor: "uploadDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 140,
        Cell: DateTimeRangeCell
      },
    ]
  );

  const updateData = (rowIndex, columnID, value) => {
    let updatedDocuments = [...loadDocuments];

    updatedDocuments[rowIndex][columnID] = value;
    setLoadDocuments(updatedDocuments);
  };

  const getDocuments = () => {
    ImageService.GetDocuments(instance, "O", activeLoad.id).then(res => {
      res.forEach(d => { d.uploadedBy = "McLeod"; });
      setLoadDocuments(res);
      setHasNoDocuments(res.length === 0);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  };

  useEffect(() => {
    if (loadDocuments.length > 0) {
      //Hint 1=bol, 10=invoice, 3=lumper
      setIsBol(loadDocuments.find(d => d.documentTypeId === "1"));
      setIsPod(false);
      setIsInvoice(loadDocuments.find(d => d.documentTypeId === "10"));
      setIsLumper(loadDocuments.find(d => d.documentTypeId === "3"));
    }
  }, [loadDocuments]);

  useEffect(() => {
    if (refreshDocuments) {
      setRefreshDocuments(false);
      getDocuments();
    }
  }, [refreshDocuments]);

  useEffect(() => {
    getDocuments();
  }, []);

  return (
    <Box className="Load-profile-component">
      <Box className="Load-profile-component-header">
        <Typography variant="h3" color="black_opacity.100">
          Documents
        </Typography>
        <Box sx={{ marginLeft: "auto" }}>
          <LoadProfileUploadDocumentModal
            activeLoad={activeLoad}
            acceptedFiles={acceptedFiles}
            setRefreshDocuments={setRefreshDocuments}
          />
        </Box>
      </Box>
      <Box className="Load-profile-component-header" sx={{ justifyContent: "start" }}>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "4px",
          border: "1px solid",
          borderColor: "black_opacity.10",
          marginRight: "10px",
          padding: "6px 12px"
        }}>
          {isBol ?
            <CheckMarkIcon sx={{ fontSize: "16px", color: "success.70", marginRight: "10px" }} /> :
            <ErrorIcon sx={{ fontSize: "16px", color: "error.70", marginRight: "10px" }} />
          }
          <Typography variant="h4" color="black_opacity.100">
            BOL
          </Typography>
        </Box>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "4px",
          border: "1px solid",
          borderColor: "black_opacity.10",
          marginRight: "10px",
          padding: "6px 12px"
        }}>
          {isPod ?
            <CheckMarkIcon sx={{ fontSize: "16px", color: "success.70", marginRight: "10px" }} /> :
            <ErrorIcon sx={{ fontSize: "16px", color: "error.70", marginRight: "10px" }} />
          }
          <Typography variant="h4" color="black_opacity.100">
            POD
          </Typography>
        </Box>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "4px",
          border: "1px solid",
          borderColor: "black_opacity.10",
          marginRight: "10px",
          padding: "6px 12px"
        }}>
          {isInvoice ?
            <CheckMarkIcon sx={{ fontSize: "16px", color: "success.70", marginRight: "10px" }} /> :
            <ErrorIcon sx={{ fontSize: "16px", color: "error.70", marginRight: "10px" }} />
          }
          <Typography variant="h4" color="black_opacity.100">
            Carrier Invoice
          </Typography>
        </Box>
        {carrierExtraPay.length > 0 && carrierExtraPay.some(({ deductCodeId }) => deductCodeId === "LUM") &&
          <Box sx={{
            display: "flex",
            alignItems: "center",
            borderRadius: "4px",
            border: "1px solid",
            borderColor: "black_opacity.10",
            marginRight: "10px",
            padding: "6px 12px"
          }}>
            {isLumper ?
              <CheckMarkIcon sx={{ fontSize: "16px", color: "success.70", marginRight: "10px" }} /> :
              <ErrorIcon sx={{ fontSize: "16px", color: "error.70", marginRight: "10px" }} />
            }
            <Typography variant="h4" color="black_opacity.100">
              Lumper Receipt
            </Typography>
          </Box>
        }
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box {...getRootProps({ className: "dropzone" })}>
          {hasNoDocuments ? (
            <Typography color="black_solid.50" sx={{ padding: "20px", textAlign: "center" }}>
              There are no documents for this order
            </Typography>
          ) : (
            <CustomizedTable
              id="carrier-contacts"
              columns={columns}
              data={documents}
              updateData={updateData}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

LoadProfileDocuments.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  carrierExtraPay: PropTypes.array
};